import { queryClient } from "@istari-frontend/api-client";
import { reportReadFailure } from "@istari-frontend/shared";
import { type InfiniteData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  appendConversationMessage,
  type ConversationPage,
  createConversation,
  listConversationMessages,
  listConversations,
  renameConversation,
  setConversationArchived,
  setConversationTranscript,
} from "~/ai-chat/data-source/conversations-api";
import type { Conversation, ConversationMessage } from "~/ai-chat/model/conversation";

interface ConversationListParams {
  q?: string;
  includeArchived?: boolean;
}

export const conversationKeys = {
  all: ["ai-conversations"] as const,
  lists: () => [...conversationKeys.all, "list"] as const,
  list: ({ q = "", includeArchived = false }: ConversationListParams = {}) =>
    [...conversationKeys.lists(), q, includeArchived] as const,
  messages: (conversationId: string) => [...conversationKeys.all, conversationId, "messages"] as const,
};

const CONVERSATIONS_PAGE_SIZE = 30;

export function useConversations(params: ConversationListParams = {}) {
  const q = params.q?.trim() ?? "";
  const includeArchived = params.includeArchived ?? false;
  return useInfiniteQuery({
    queryKey: conversationKeys.list({ q, includeArchived }),
    queryFn: ({ pageParam }) =>
      listConversations({ q, includeArchived, cursor: pageParam, size: CONVERSATIONS_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
}

// Flatten the infinite pages into a single list for rendering.
export function flattenConversations(data: InfiniteData<ConversationPage> | undefined): Conversation[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

// Apply an update across every cached list variant (search terms / archived
// filter each get their own cache entry) so an optimistic edit isn't stranded.
function patchCachedConversation(conversationId: string, patch: (conversation: Conversation) => Conversation) {
  queryClient.setQueriesData<InfiniteData<ConversationPage>>({ queryKey: conversationKeys.lists() }, (data) =>
    data === undefined
      ? data
      : {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((conversation) =>
              conversation.id === conversationId ? patch(conversation) : conversation,
            ),
          })),
        },
  );
}

// Only the unfiltered active list is patched: whether a search variant matches the new title is the
// server's call, so those (and the archived variant) wait for the invalidation refetch.
function insertCachedConversation(created: Conversation) {
  queryClient.setQueryData<InfiniteData<ConversationPage>>(
    conversationKeys.list({ q: "", includeArchived: false }),
    (data) => {
      if (data === undefined) return data;
      if (data.pages.some((page) => page.items.some((conversation) => conversation.id === created.id))) return data;
      const [first, ...rest] = data.pages;
      if (first === undefined) return data;
      return { ...data, pages: [{ ...first, items: [created, ...first.items] }, ...rest] };
    },
  );
}

export function useCreateConversation() {
  return useMutation({
    mutationFn: (title: string | null) => createConversation(title),
    onSuccess: (created) => {
      insertCachedConversation(created);
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

type RenameConversationParams = {
  conversationId: string;
  title: string | null;
};

export function useRenameConversation() {
  return useMutation({
    mutationFn: ({ conversationId, title }: RenameConversationParams) => renameConversation(conversationId, title),
    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });
      const previous = queryClient.getQueriesData<InfiniteData<ConversationPage>>({
        queryKey: conversationKeys.lists(),
      });
      patchCachedConversation(conversationId, (conversation) => ({ ...conversation, title }));
      return { previous };
    },
    onError: (_error, _params, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

type SetTranscriptParams = {
  conversationId: string;
  transcriptResourceId: string | null;
};

// Links (or clears) the resource a conversation's saved transcript became. Patches the cache so a following
// Share reads the link without a refetch (the patch leaves `updated` alone, so list order is unchanged).
// Reports failures here so every caller is covered, and still applies the intended value locally on error so
// a failed clear doesn't strand a dead pointer in the cache.
export function useSetConversationTranscript() {
  return useMutation({
    mutationFn: ({ conversationId, transcriptResourceId }: SetTranscriptParams) =>
      setConversationTranscript(conversationId, transcriptResourceId),
    onSuccess: (updated) => {
      patchCachedConversation(updated.id, (conversation) => ({
        ...conversation,
        transcriptResourceId: updated.transcriptResourceId,
      }));
    },
    onError: (error, { conversationId, transcriptResourceId }) => {
      reportReadFailure("ai-chat.set-transcript", error, { conversationId });
      patchCachedConversation(conversationId, (conversation) => ({ ...conversation, transcriptResourceId }));
    },
  });
}

type ArchiveConversationParams = {
  conversationId: string;
  archived: boolean;
};

export function useArchiveConversation() {
  return useMutation({
    mutationFn: ({ conversationId, archived }: ArchiveConversationParams) =>
      setConversationArchived(conversationId, archived),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
    // Without feedback the archive action looks like it did nothing when the request fails.
    onError: (_error, { archived }) => {
      toast.error(archived ? "Couldn't archive the conversation." : "Couldn't unarchive the conversation.");
    },
  });
}

// Call when a turn has settled: the server has just written the assistant row and terminal status,
// so the cached messages — and any request for them already in flight (a first load or a refetch,
// on screen or not) — predate the truth. A plain invalidate would let such a request finish later
// with the pre-commit rows and be taken for fresh; cancelling first discards it (the state reverts
// inside the call, so the refetch below starts clean). The refetch covers off-screen queries that
// have loaded once, so a return finds the committed rows waiting; a cancelled first load is simply
// repeated when the conversation is next shown.
export function refreshAfterTurn(conversationId: string) {
  const messagesKey = conversationKeys.messages(conversationId);
  void queryClient.cancelQueries({ queryKey: messagesKey });
  void queryClient.invalidateQueries({ queryKey: messagesKey, refetchType: "all" });
  void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.messages(conversationId ?? ""),
    queryFn: () => listConversationMessages(conversationId ?? ""),
    enabled: conversationId !== null,
  });
}

type AppendMessageParams = {
  conversationId: string;
  role: ConversationMessage["role"];
  content: string;
};

export function useAppendConversationMessage() {
  return useMutation({
    mutationFn: ({ conversationId, role, content }: AppendMessageParams) =>
      appendConversationMessage(conversationId, role, content),
    onSuccess: (_message, { conversationId }) => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}
